const ENV = process.env.NODE_ENV || 'development'
const IS_PROD = ENV === 'production'

const DEFAULT_LOG_SAMPLE_RATE = IS_PROD ? 0.02 : 1
const DEFAULT_ERROR_SAMPLE_RATE = IS_PROD ? 1 : 1
const DEFAULT_REPORT_URL = (process.env.VUE_APP_LOG_REPORT_URL || '').trim()
const REPORT_ENABLED = IS_PROD && DEFAULT_REPORT_URL.length > 0

const rawConsole = {
  debug: typeof console !== 'undefined' ? console.debug.bind(console) : () => {},
  info: typeof console !== 'undefined' ? console.info.bind(console) : () => {},
  warn: typeof console !== 'undefined' ? console.warn.bind(console) : () => {},
  error: typeof console !== 'undefined' ? console.error.bind(console) : () => {}
}

function parseSampleRate (value, fallback) {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  if (n <= 0) return 0
  if (n >= 1) return 1
  return n
}

const LOG_SAMPLE_RATE = parseSampleRate(process.env.VUE_APP_LOG_SAMPLE_RATE, DEFAULT_LOG_SAMPLE_RATE)
const ERROR_SAMPLE_RATE = parseSampleRate(process.env.VUE_APP_ERROR_SAMPLE_RATE, DEFAULT_ERROR_SAMPLE_RATE)

function nowIso () {
  return new Date().toISOString()
}

function shouldSample (rate) {
  if (rate >= 1) return true
  if (rate <= 0) return false
  return Math.random() < rate
}

function safeStringify (value) {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return value

  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    const message = e && e.message ? e.message : 'unknown'
    return `[unserializable:${message}]`
  }
}

function normalizeArgs (args) {
  return args.map((arg) => safeStringify(arg))
}

function buildMeta () {
  if (typeof window === 'undefined') {
    return {}
  }
  return {
    href: window.location.href,
    userAgent: window.navigator.userAgent
  }
}

function report (level, args) {
  if (typeof window === 'undefined') return
  if (!REPORT_ENABLED) return

  const rate = level === 'error' ? ERROR_SAMPLE_RATE : LOG_SAMPLE_RATE
  if (!shouldSample(rate)) return

  const payload = {
    level,
    env: ENV,
    timestamp: nowIso(),
    message: normalizeArgs(args),
    meta: buildMeta()
  }

  try {
    const body = JSON.stringify(payload)
    if (window.navigator && typeof window.navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      window.navigator.sendBeacon(DEFAULT_REPORT_URL, blob)
      return
    }
    if (typeof window.fetch === 'function') {
      window.fetch(DEFAULT_REPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {})
    }
  } catch (e) {
    // avoid recursive logger call
  }
}

function debug (...args) {
  if (!IS_PROD) {
    rawConsole.debug(...args)
  }
  report('debug', args)
}

function info (...args) {
  if (!IS_PROD) {
    rawConsole.info(...args)
  }
  report('info', args)
}

function warn (...args) {
  if (!IS_PROD) {
    rawConsole.warn(...args)
  }
  report('warn', args)
}

function error (...args) {
  if (!IS_PROD) {
    rawConsole.error(...args)
  }
  report('error', args)
}

const logger = {
  debug,
  info,
  warn,
  error
}

export default logger
